import React, { useMemo, useCallback } from 'react';
import styles from './index.module.scss';
import { Icon } from '@components/Icon';
import { ICON_TYPES, IconType } from '@components/Icon/IconAssets';
import { useTranslation } from 'react-i18next';
import { OrderStatusGetV1ResponseOrderStatus } from '@openapi/orderstatus/order_status';
import { Loading } from '@components/Loading';

type Props = {
  statusType?: OrderStatusGetV1ResponseOrderStatus;
  updateStatus: () => void;
  isLoading?: boolean;
};

export const StatusBox: React.FC<Props> = ({
  statusType = OrderStatusGetV1ResponseOrderStatus.NotFound,
  updateStatus,
  isLoading = false,
}) => {
  const { t } = useTranslation();

  type StatusMeta = {
    messageKey: string;
    message: string;
    canStatusUpdate: boolean;
    iconType: IconType;
    bgColor: string;
    border: string;
    color: string;
  };

  const getStatusMeta = useCallback(
    (
      statusType: OrderStatusGetV1ResponseOrderStatus,
      t: (key: string) => string,
    ): StatusMeta => {
      const statusMap: Record<
        OrderStatusGetV1ResponseOrderStatus,
        Omit<StatusMeta, 'message'>
      > = {
        [OrderStatusGetV1ResponseOrderStatus.Prepared]: {
          messageKey: 'prepareStatusMessage',
          canStatusUpdate: true,
          iconType: ICON_TYPES.TIMER,
          bgColor: '#fdf2ec',
          border: '1px solid #ec5c0c',
          color: '#EC5C0C',
        },
        [OrderStatusGetV1ResponseOrderStatus.Ready]: {
          messageKey: 'readyStatusMessage',
          canStatusUpdate: true,
          iconType: ICON_TYPES.BELL,
          bgColor: '#F7FFF9',
          border: '1px solid #28A745',
          color: '#28A745',
        },
        [OrderStatusGetV1ResponseOrderStatus.Delivered]: {
          messageKey: 'completeStatusMessage',
          canStatusUpdate: false,
          iconType: ICON_TYPES.CHECK,
          bgColor: '#F6F6F6',
          border: '1px solid #888888',
          color: '#888888',
        },
        [OrderStatusGetV1ResponseOrderStatus.Cancelled]: {
          messageKey: 'cancelStatusMessage',
          canStatusUpdate: false,
          iconType: ICON_TYPES.CANCEL,
          bgColor: '#666666',
          border: '0',
          color: '#FFFFFF',
        },
        [OrderStatusGetV1ResponseOrderStatus.NotFound]: {
          messageKey: 'noMatchStatusMessage',
          canStatusUpdate: false,
          iconType: ICON_TYPES.ALERT,
          bgColor: '#D84448',
          border: '0',
          color: '#FFFFFF',
        },
      };

      const meta =
        statusMap[statusType] ??
        statusMap[OrderStatusGetV1ResponseOrderStatus.NotFound];

      return {
        message: t(meta.messageKey),
        ...meta,
      };
    },
    [],
  );

  const statusMeta = useMemo(
    () => getStatusMeta(statusType, t),
    [statusType, t, getStatusMeta],
  );

  const { message, canStatusUpdate, iconType, bgColor, border, color } =
    statusMeta;

  if (isLoading)
    return (
      <div
        className={styles.container}
        style={{
          border: '1px solid #c4c4c4',
        }}
      >
        <Loading />
      </div>
    );

  return (
    <div
      className={styles.container}
      style={{
        backgroundColor: bgColor,
        border: border,
        color: color,
        cursor: canStatusUpdate ? 'pointer' : 'default',
      }}
      onClick={canStatusUpdate ? updateStatus : undefined}
    >
      <div className={styles.container__messageBox}>
        <Icon type={iconType} color={color} />

        <div>{message}</div>
      </div>
      {canStatusUpdate && (
        <div className={styles.container__statusUpdateBox}>
          <div>{t('updateStatus')}</div>
          <Icon type={ICON_TYPES.UPDATE} color={color} />
        </div>
      )}
    </div>
  );
};
